import { motion } from "framer-motion";

const VERTICALS = [
  {
    id: "commercial",
    number: "01",
    title: "Commercial Products",
    desc: "Off-the-shelf brass, bronze and aluminium valves for plumbing, HVAC and light-industrial use — no engineering sign-off required. If your spec sheet is simple, this is where you start and finish.",
    specs: [
      "Ball · Gate · Globe · Check · Y-Strainer (Brass & Bronze)",
      "Aluminium Butterfly Valves ¼\" – 28\"",
      "ASME 150# – 300#",
    ],
    image:
      "https://customer-assets-m6fa6gv7.emergentagent.net/job_a0d6c2d7-fbf4-4969-b3b8-47e87e3179c6/artifacts/7672nr0i_Al-BFV.jpg",
  },
  {
    id: "industrial",
    number: "02",
    title: "Industrial Products",
    desc: "KITZ industrial valves are selected to match your process conditions — pressure class, actuation, and certification. Every choice is reviewed by our engineering team to ensure reliability in demanding plant environments.",
    specs: [
      "Ball Valves ¼\" – 16\", ASME 150# – 2500#",
      "Butterfly Valves ¼\" – 28\"",
      "GGC · Flush Bottom · IBR Ball Valve",
      "Pneumatic & Electric Actuated · Oil-Free",
    ],
    image:
      "https://customer-assets-m6fa6gv7.emergentagent.net/job_a0d6c2d7-fbf4-4969-b3b8-47e87e3179c6/artifacts/ompq1vas_Industrial%20Valve.jpg",
  },
  {
    id: "semiconductor",
    number: "03",
    title: "Semiconductor Products",
    desc: "Ultra high purity, cleanroom grade KITZ SCT components engineered for fab environments — where contamination control and outgassing limits define the product, not the price. A dedicated line for a dedicated customer.",
    specs: [
      "Ultra-High Purity Valves & Fittings",
      "Vacuum Components",
      "PFA Valves & Fittings",
      "Antistatic Unit",
    ],
    image:
      "https://customer-assets-m6fa6gv7.emergentagent.net/job_a0d6c2d7-fbf4-4969-b3b8-47e87e3179c6/artifacts/alojnbpg_image002.jpg",
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 50 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] } },
};

const Products = () => (
  <section id="products" className="relative bg-slate-50 border-t border-slate-200 py-28 sm:py-36">
    <div className="max-w-[1600px] mx-auto px-6 sm:px-10">
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
        variants={fadeUp}
        className="mb-20 max-w-2xl"
      >
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#1C8FC4] mb-6">02 / Product Lines</p>
        <h2 className="font-heading font-bold uppercase text-[#1A2E4A] text-4xl sm:text-5xl tracking-tight">
          Three verticals. One selection each.
        </h2>
        <p className="mt-5 font-body text-slate-600 text-base leading-relaxed">
          Most customers work within a single vertical — we built our product line the same way.
        </p>
      </motion.div>

      <div className="flex flex-col gap-24 sm:gap-32">
        {VERTICALS.map((v, i) => (
          <motion.div
            key={v.id}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeUp}
            data-testid={`product-vertical-${v.id}`}
            className="flex flex-col lg:flex-row gap-10 lg:gap-16 items-center"
          >
            <div className={`w-full lg:w-5/12 relative ${i % 2 === 1 ? "lg:order-2" : "lg:order-1"}`}>
              <div className="relative bg-white border border-slate-200 p-2 pt-16 sm:pt-20">
                <span className="absolute top-2 left-4 z-10 font-display text-6xl sm:text-7xl text-[#1A2E4A] leading-none select-none bg-white px-1">
                  {v.number}
                </span>
                <div className="overflow-hidden aspect-[4/5]">
                  <img
                    src={v.image}
                    alt={v.title}
                    className="w-full h-full object-cover grayscale-[15%] hover:grayscale-0 hover:scale-105 transition-all duration-700"
                  />
                </div>
              </div>
            </div>

            <div className={`w-full lg:w-7/12 ${i % 2 === 1 ? "lg:order-1" : "lg:order-2"}`}>
              <h3 className="font-heading font-bold uppercase text-[#1A2E4A] text-3xl sm:text-4xl tracking-tight mb-5">
                {v.title}
              </h3>
              <p className="font-body text-slate-700 text-base sm:text-lg leading-relaxed mb-6">{v.desc}</p>
              <ul className="flex flex-col gap-3">
                {v.specs.map((spec) => (
                  <li
                    key={spec}
                    className="font-body text-sm sm:text-base text-slate-700 border-l-2 border-slate-300 pl-4 py-1"
                  >
                    {spec}
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

export default Products;
